import { Translate } from 'i18n';
import { FC, useId } from 'react';
import styled, { css } from 'styled-components';
import { useColorTheme, useThemeToggle } from 'students/views/shared/providers/Theme';
import { menuItemRow } from './profileMenuStyles';

const ICON_SIZE = css`
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PRESSED_SEGMENT_SHADOW = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';

export const AppearanceSwitch: FC = () => {
  const { theme, switchTheme } = useColorTheme();
  const { options, CurrentIcon } = useThemeToggle();
  const labelId = useId();

  return (
    <SRow>
      <SCurrentIcon>
        <CurrentIcon />
      </SCurrentIcon>
      <SLabel id={labelId}>
        <Translate
          str="frontend.profile.navigation.appearance"
          params={{ defaultValue: 'Appearance' }}
        />
      </SLabel>

      <SSegments role="group" aria-labelledby={labelId}>
        {options.map(({ value, label, Icon }) => (
          <SSegmentButton
            key={value}
            type="button"
            aria-pressed={theme === value}
            aria-label={label}
            onClick={() => switchTheme(value)}
          >
            <SSegmentIcon>
              <Icon />
            </SSegmentIcon>
          </SSegmentButton>
        ))}
      </SSegments>
    </SRow>
  );
};

const SRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 8px;
  ${menuItemRow}
`;

const SCurrentIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  ${ICON_SIZE}
`;

const SLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 17px;
  letter-spacing: -0.02em;
`;

const SSegments = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 2px;
  margin-inline-start: auto;
  padding: 2px;
  border-radius: 8px;
  background-color: var(--ln-menu-hover);
`;

const SSegmentButton = styled.button`
  display: flex;
  padding: 6px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--color-black);
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background-color: rgb(255 255 255 / 0.6);
  }

  &[aria-pressed='true'] {
    background-color: var(--color-white);
    box-shadow: ${PRESSED_SEGMENT_SHADOW};
  }

  &:focus-visible {
    outline: 2px solid var(--color-blue-btn-focus-outline-color);
    outline-offset: -2px;
  }
`;

const SSegmentIcon = styled.span`
  display: flex;
  ${ICON_SIZE}
`;
